/**
 * ConnectionCardCompact Component
 *
 * Compact connection card with consistent styling for use in both
 * StartPage and Connections Dialog. Shows engine icon, connection info,
 * status, and action buttons.
 */

import { useMemo } from 'react';
import { Server, Database, AlertCircle, Plug, Unplug, Pencil, Waypoints } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { ConnectionEngineIcon, getEngineShortLabel } from './ConnectionEngineIcon';
import { ConnectionColorAccent } from './ConnectionColorAccent';
import { cn } from '../../../lib/utils';
import type { Connection } from '../../../types/electron';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { MiddleTruncate } from '@/components/ui/middle-truncate';
import { connectionServerLine } from '../utils/connectionServerLine';
import { ReadOnlyConnectionBadge } from './ReadOnlyConnectionBadge';
import { safeConnectionName, safeServerText } from '@/types/safeConnectionDisplay';
export type ConnectionCardCompactStatus =
  | 'connected'
  | 'disconnected'
  | 'connecting'
  | 'disconnecting'
  | 'error';

export type ConnectionCardCompactProps = {
  connection: Connection;
  status: ConnectionCardCompactStatus;
  /** SSH tunnel state, when a tunnel is configured. Sourced from checkConnectionStatus. */
  tunnelState?: 'up' | 'down' | 'connecting' | 'error';
  database?: string;
  errorMessage?: string;
  onConnect: () => void;
  onDisconnect: () => void;
  onEdit?: () => void;
  /** Handler for clicking on connection info (server address) to show connection info dialog */
  onConnectionInfo?: () => void;
  /** Quick actions slot - rendered below the action button when connected */
  quickActions?: React.ReactNode;
  /** Compact mode - smaller padding and inline layout for disconnected cards */
  compact?: boolean;
  /** Whether this card is keyboard-focused */
  isFocused?: boolean;
  /** Keyboard event handler for arrow key navigation */
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
};

/**
 * Status indicator dot with tooltip
 *
 * T6.1c (G-6.3) — THESE FOUR COLOURS STAY HARDCODED. Ruled once; do not
 * "fix" them onto tokens without re-opening the ruling.
 *
 * The rule this wave applies to every semantic colour axis: a member moves
 * onto a design token only where a token already carries that exact meaning
 * on that surface. That rule tokenised most of Data Compare's diff-type axis,
 * and it leaves this one alone, for three stated reasons:
 *
 * 1. `--energy` is the only token that could mean "connecting", and the
 *    locked accent system FORBIDS orange on "engine/connection badges" by
 *    name. The one plausible token is explicitly out of bounds here.
 * 2. There is no green token and no amber token, so "connected" and
 *    "connecting" have no destination at all — the same gap that keeps Data
 *    Compare's Added green hardcoded.
 * 3. `--destructive` could take "error", but on dark it measures 1.82:1
 *    against `--background` (painted #861118): it is tuned as a FILL sitting
 *    under `--destructive-foreground`, not as a standalone 8px dot. Swapping
 *    it in would dim the one state that most needs to be seen.
 *
 * Scope matters too. This is not one component's private palette: the same
 * green/amber/grey/red traffic-light vocabulary renders in ~40 places —
 * job-scheduler, pl-sql-debugger, data-pump, instance-monitor,
 * session-browser, `ConnectionSelectorDialog`, `AiToolbarButton`. Tokenising
 * this one instance would produce exactly the drift this wave exists to
 * remove: one status dot on tokens and thirty-nine off them.
 *
 * So connection status is a SANCTIONED NON-TOKEN AXIS, consistent with the
 * diff-category axis being one. If a future reviewer disagrees, the fix is a
 * deliberate `--status-ok` / `--status-warn` token family decided at the same
 * review tier that locked the two-family system — not a unilateral swap here.
 */
function StatusIndicator({ status }: { status: ConnectionCardCompactStatus }) {
  const config = useMemo(() => {
    switch (status) {
      case 'connected':
        return { className: 'bg-green-500', label: 'Connected' };
      case 'disconnected':
        return { className: 'bg-gray-400', label: 'Disconnected' };
      case 'connecting':
      case 'disconnecting':
        return {
          className: 'bg-yellow-500 animate-pulse',
          label: status === 'connecting' ? 'Connecting...' : 'Disconnecting...',
        };
      case 'error':
        return { className: 'bg-red-500', label: 'Error' };
      default:
        return { className: 'bg-gray-400', label: 'Unknown' };
    }
  }, [status]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn('h-2 w-2 rounded-full flex-shrink-0', config.className)}
            aria-label={config.label}
            data-testid="connection-status-indicator"
          />
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ConnectionCardCompact({
  connection,
  status,
  tunnelState,
  database,
  errorMessage,
  onConnect,
  onDisconnect,
  onEdit,
  onConnectionInfo,
  quickActions,
  compact = false,
  isFocused,
  onKeyDown,
}: ConnectionCardCompactProps) {
  const isOperating = status === 'connecting' || status === 'disconnecting';
  const isActive = status === 'connected';
  const displayName = safeConnectionName(connection);
  // PP-27 — a plain "disconnected" row in the compact (Home-page cluster)
  // list reads as available-but-inactive, not as broken and not as live.
  // Scoped to `compact` only: the Connections Dialog's own (non-compact)
  // list keeps its existing look untouched. `error`/`connecting`/
  // `disconnecting` are active states that need attention, so they stay
  // at full opacity — only the truly idle status gets dimmed.
  const isDimmed = compact && status === 'disconnected';

  // Build server info string based on engine (round B U6: no ":0" port, an Azure Storage account reads "account · auth")
  const serverInfo = useMemo(() => connectionServerLine(connection), [connection]);

  // C2 R6 / N3: the read-only tag, the engine label and the SSH glyph. A card puts them on its second line, so the
  // alias keeps the full width of the header (two lines, full name as tooltip) — squeezed beside them it wrapped to one
  // character per line ("C / o."). A compact row (one line, middle-truncated name) keeps them inline. The engine label
  // is plain muted text (the /60 tint measured 2.8:1).
  const badges = (
    <>
      {connection.readOnly && <ReadOnlyConnectionBadge testId="connection-read-only-badge" />}
      <span className="text-[10px] text-muted-foreground flex-shrink-0" data-testid="connection-engine-label">
        {getEngineShortLabel(connection.engine)}
      </span>
      {connection.sshTunnel?.enabled && (() => {
        // F8 — only show the destructive (red) coloring when there is
        // an ACTUAL tunnel problem. When the user hasn't attempted to
        // connect yet, `tunnelState` is undefined; rendering a red
        // glyph misleads them into thinking SSH is broken. The
        // muted-foreground (informational) treatment is correct for
        // both "never connected yet" and "tunnel currently up".
        //
        // M14 — uses lucide `Waypoints` (multi-hop network path) rather
        // than `Lock`, which is reserved app-wide for TLS/encryption.
        // Conflating the two icons made users read the glyph as
        // "DB connection is encrypted" instead of "SSH tunnel".
        const isHealthy = tunnelState === undefined || tunnelState === 'up';
        const ariaLabel = isHealthy ? 'SSH tunnel active' : 'SSH tunnel disconnected';
        return (
          <span
            data-testid="ssh-tunnel-glyph"
            role="img"
            aria-label={ariaLabel}
            className={cn(
              'ml-1 inline-flex items-center flex-shrink-0',
              isHealthy ? 'text-muted-foreground' : 'text-destructive'
            )}
            title={
              isHealthy
                ? `SSH tunnel · ${connection.sshTunnel.user}@${safeServerText(connection.sshTunnel.host)}`
                : 'SSH tunnel disconnected'
            }
          >
            <Waypoints className="h-3 w-3" />
          </span>
        );
      })()}
    </>
  );

  return (
    <div
      className={cn(
        'relative isolate flex flex-col rounded-md border bg-card transition-[color,border-color,box-shadow,opacity] outline-none',
        compact ? 'p-2' : 'p-3',
        isFocused
          ? 'border-primary ring-2 ring-ring shadow-sm'
          : isActive
            ? 'border-primary/50 shadow-sm'
            : 'border-border hover:border-border/80 hover:shadow-sm',
        isDimmed && 'opacity-70'
      )}
      data-testid={`connection-item-${connection.id}`}
      data-connection-id={connection.id}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <ConnectionColorAccent color={connection.color} compact={compact} />
      {/* Header row: Engine icon + Name + Status + Action buttons */}
      <div className="flex items-center gap-2">
        {/* Clickable area: Engine icon + Name + Engine label */}
        <div
          className={cn(
            'flex items-center gap-2 flex-1 min-w-0',
            onConnectionInfo && 'cursor-pointer hover:opacity-80 transition-opacity'
          )}
          onClick={onConnectionInfo}
          role={onConnectionInfo ? 'button' : undefined}
          tabIndex={onConnectionInfo ? 0 : undefined}
          onKeyDown={onConnectionInfo ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onConnectionInfo();
            }
          } : undefined}
          title={onConnectionInfo ? 'Click for connection info' : displayName}
        >
          <ConnectionEngineIcon
            engine={connection.engine}
            size={compact ? 'sm' : 'md'}
            className="flex-shrink-0"
          />
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <StatusIndicator status={status} />
            {/* Round B U6: long names stay distinguishable — the card wraps to two lines, a compact row keeps the end
                of the name visible (middle ellipsis); the full name is the tooltip either way. */}
            {/* Z9 (last UI/UX pass): the compact heading clips its own text — a short alias's inline span used to
                overflow it and paint over the engine label. */}
            <h3
              className={cn('font-medium flex-1 min-w-0', compact ? 'text-xs overflow-hidden' : 'text-sm line-clamp-2 break-words')}
              title={displayName}
            >
              {compact ? <MiddleTruncate text={displayName} /> : displayName}
            </h3>
            {compact && badges}
          </div>
        </div>

        {/* PP-27 — fixed-width badge slot, compact rows only. Reserves the
            same width whether or not a badge renders, so the name column
            (flex-1 above) always has the same amount of room to truncate
            into and the badge/button columns after it land at a consistent
            x-position down the whole disconnected-cluster list. Only the
            compact list needs this: the non-compact Connections Dialog
            keeps its existing full error line below (see `!compact` guard
            further down). */}
        {compact && (
          <div className="w-4 shrink-0 flex items-center justify-center" data-testid="connection-badge-slot">
            {status === 'error' && errorMessage && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span role="img" aria-label={`Connection error: ${errorMessage}`}>
                      <AlertCircle className="h-3.5 w-3.5 text-destructive" data-testid="connection-error-badge" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px]">
                    <p>{errorMessage}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        {/* Action buttons in upper right */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Connected: Edit button (left) + Disconnect button (right) */}
          {isActive || status === 'disconnecting' ? (
            <>
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={(compact ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0') + ' active:scale-[0.96] transition-[color,box-shadow,scale]'}
                  onClick={onEdit}
                  disabled={isOperating}
                  data-testid={`edit-button-${connection.id}`}
                  title="Edit connection"
                >
                  <Pencil className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className={(compact ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0') + ' active:scale-[0.96] transition-[color,box-shadow,scale]'}
                onClick={onDisconnect}
                disabled={isOperating}
                data-testid={`disconnect-button-${connection.id}`}
                title="Disconnect"
              >
                {status === 'disconnecting' ? (
                  <RandomDotmSpinner size={compact ? 12 : 14} />
                ) : (
                  <Unplug className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                )}
              </Button>
            </>
          ) : (
            /* Disconnected: Edit button (if provided) + Connect button */
            <>
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={(compact ? 'h-6 w-6 p-0' : 'h-7 w-7 p-0') + ' active:scale-[0.96] transition-[color,box-shadow,scale]'}
                  onClick={onEdit}
                  disabled={isOperating}
                  data-testid={`edit-button-${connection.id}`}
                  title="Edit connection"
                >
                  <Pencil className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                </Button>
              )}
              {compact ? (
                <Button
                  variant="default"
                  size="sm"
                  className="h-6 w-6 p-0 active:scale-[0.96] transition-[color,box-shadow,scale]"
                  onClick={onConnect}
                  disabled={isOperating}
                  data-testid={`connect-button-${connection.id}`}
                  title="Connect"
                >
                  {status === 'connecting' ? (
                    <RandomDotmSpinner size={12} />
                  ) : (
                    <Plug className="h-3 w-3" />
                  )}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="h-7 px-2 active:scale-[0.96] transition-[color,box-shadow,scale]"
                  onClick={onConnect}
                  disabled={isOperating}
                  data-testid={`connect-button-${connection.id}`}
                >
                  {status === 'connecting' ? (
                    <RandomDotmSpinner size={14} className="mr-1" />
                  ) : (
                    <Plug className="h-3.5 w-3.5 mr-1" />
                  )}
                  Connect
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Server info + Database (full width row) - hidden in compact mode */}
      {/* F3 (last UI/UX pass), every engine: the tags wrap under the server line when both do not fit — squeezed beside
          it they cut the authentication to one letter ("jamsqlazstfiles · A…"). */}
      {!compact && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-muted-foreground mt-1',
            onConnectionInfo && 'cursor-pointer hover:text-foreground transition-colors'
          )}
          data-testid="connection-card-server-line"
          onClick={onConnectionInfo}
          role={onConnectionInfo ? 'button' : undefined}
          tabIndex={onConnectionInfo ? 0 : undefined}
          onKeyDown={onConnectionInfo ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onConnectionInfo();
            }
          } : undefined}
          title={onConnectionInfo ? 'Click for connection info' : serverInfo}
        >
          <span className="flex min-w-0 max-w-full items-center gap-1" data-testid="connection-card-server">
            <Server className="h-3 w-3 flex-shrink-0" />
            <span className="truncate" title={serverInfo}>
              {serverInfo}
            </span>
          </span>
          {isActive && database && (
            <span className="flex min-w-0 max-w-full items-center gap-1">
              <span className="text-muted-foreground/50">/</span>
              <Database className="h-3 w-3 flex-shrink-0" />
              <span className="truncate" title={database}>
                {database}
              </span>
            </span>
          )}
          <span className="ml-auto flex shrink-0 items-center gap-1.5 pl-1" data-testid="connection-card-badges">{badges}</span>
        </div>
      )}

      {/* Error message — non-compact only. Compact rows (the Home-page
          disconnected cluster) show the same message via the fixed-width
          badge slot's tooltip instead, so every row in that list keeps a
          uniform height (PP-27). The Connections Dialog list always renders
          non-compact, so its existing inline error line is unchanged. */}
      {!compact && status === 'error' && errorMessage && (
        <div className="flex items-start gap-1 text-xs text-destructive mt-1">
          <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
          <span className="line-clamp-2" title={errorMessage}>
            {errorMessage}
          </span>
        </div>
      )}

      {/* Quick actions slot (only shown when connected) */}
      {isActive && quickActions && <div className="mt-2">{quickActions}</div>}
    </div>
  );
}
